import type { WalkthroughStep } from "../types";

/**
 * Spawn Rename's tour, which only ever runs because somebody asked for it.
 *
 * This tool has no sample pack, so there is no `welcome` step announcing one
 * and no demo button to start the tour from. The one way in is the permanent
 * "Show me how this works" control in the intake row (`ShowTourButton`), and
 * the island mounts `ToolWalkthrough` in manual mode, so nothing here is ever
 * thrown over a pack a user is in the middle of renaming.
 *
 * That decides what the steps may say. The tour can be opened before a folder
 * is dropped or before a code is typed, and three of its anchors — the scope
 * line, the plan and the download — only exist once those have happened. A
 * step whose anchor is missing degrades to a centred tooltip rather than
 * throwing, so every body below reads as an explanation of that part of the
 * page, never as "look at this row".
 *
 * These steps RESTATE what the island already says; they absorb nothing. The
 * "nothing on your disk is changed" sentence, the refusal panels and the notes
 * all stay in the interface.
 */
export const steps: WalkthroughStep[] = [
  {
    id: "drop",
    title: "Where your pack goes",
    body: "A resource, a folder of them, or a zip. Text files are opened to find references; models and textures are carried into the download unread. Nothing is uploaded, and nothing on your disk is changed.",
    selector: "[data-walkthrough='srn-drop']",
    placement: "bottom",
  },
  {
    id: "scope",
    title: "What was read",
    body: "Once a pack is in, this line counts what was opened as text and what was only listed, and anything that could not be read is named under it. A code that lives only in a file this pass did not open is not renamed there.",
    selector: "[data-walkthrough='srn-scope']",
    placement: "bottom",
  },
  {
    id: "rows",
    title: "One row per spawn code",
    body: "Old code on the left, new code on the right. Every field anchored on that code — `modelName`, `txdName`, `handlingId`, the streamed files, `vehicle_names.lua` — changes together. Typing runs nothing.",
    selector: "[data-walkthrough='srn-rows']",
    placement: "top",
  },
  {
    id: "plan",
    title: "The dry run is the whole plan",
    body: "Collisions, refused names and codes that matched nothing show up here before the button is live, and any collision or refused name blocks the whole batch. Every text change is a diff you can read line by line.",
    selector: "[data-walkthrough='srn-plan']",
    placement: "top",
  },
  {
    id: "download",
    title: "A new zip, never your folder",
    body: "The download is your pack with the renames applied, plus a manifest of every file moved and every line changed, so the rename can be undone by hand.",
    selector: "[data-walkthrough='srn-download']",
    placement: "top",
  },
];

export const finishLabel = "Start renaming";
